import styled from "@emotion/styled/macro";

/* ═══════════════════════════════════════════════════════════════
   CervicalScreeningStyles — Styled components for
   Cervical Cancer Screening Module (Task 19).
   Theme: Deep Rose / Magenta (#ad1457) — cervical screening
   ═══════════════════════════════════════════════════════════════ */

const PRIMARY = "#005a8b";
const PRIMARY_LIGHT = "#e8f0fe";
const PRIMARY_BG = "#f8fafd";
const SECONDARY = "#2c3e50";
const SUCCESS = "#1b5e20";
const WARNING = "#e65100";
const DANGER = "#b71c1c";
const INFO = "#0d47a1";
const PURPLE = "#4a148c";

/* ─── Page Wrapper ─── */
export const CSPageWrapper = styled("div")({
    padding: "16px 20px 32px",
    background: "#f5f6fa",
    minHeight: "calc(100vh - 120px)",
    "@media(max-width: 768px)": { padding: "10px 8px 24px" },
});

/* ─── Header ─── */
export const CSHeader = styled("div")({
    background: "linear-gradient(135deg, " + PRIMARY + " 0%, " + PRIMARY_LIGHT + " 100%)",
    borderRadius: "12px",
    padding: "20px 24px",
    marginBottom: "16px",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    "@media(max-width: 768px)": { padding: "14px 16px", flexDirection: "column", alignItems: "flex-start" },
});

export const CSHeaderLeft = styled("div")({ display: "flex", flexDirection: "column", gap: "4px" });
export const CSHeaderTitle = styled("h2")({ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#fff" });
export const CSHeaderSub = styled("p")({ margin: 0, fontSize: "0.85rem", opacity: 0.85 });

export const CSHeaderStats = styled("div")({ display: "flex", gap: "10px", flexWrap: "wrap" });

export const CSStatChip = styled("div")(function (props) {
    let bg = "rgba(255,255,255,0.15)";
    let border = "rgba(255,255,255,0.3)";
    if (props.variant === "success") { bg = "rgba(46,125,50,0.3)"; border = "rgba(46,125,50,0.5)"; }
    if (props.variant === "warning") { bg = "rgba(245,127,23,0.3)"; border = "rgba(245,127,23,0.5)"; }
    if (props.variant === "danger") { bg = "rgba(198,40,40,0.3)"; border = "rgba(198,40,40,0.5)"; }
    if (props.variant === "info") { bg = "rgba(2,119,189,0.3)"; border = "rgba(2,119,189,0.5)"; }
    return {
        padding: "6px 14px",
        borderRadius: "20px",
        fontSize: "0.78rem",
        fontWeight: 600,
        background: bg,
        border: "1px solid " + border,
        color: "#fff",
        whiteSpace: "nowrap",
    };
});

/* ─── Tab Bar ─── */
export const CSTabBar = styled("div")({
    display: "flex",
    gap: "4px",
    background: "#fff",
    borderRadius: "10px",
    padding: "4px",
    marginBottom: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflowX: "auto",
    "&::-webkit-scrollbar": { height: 0 },
});

export const CSTabBtn = styled("button")(function (props) {
    const activeBg = props.color || PRIMARY;
    return {
        flex: "1 1 auto",
        padding: "10px 18px",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "0.82rem",
        fontWeight: props.active ? 700 : 500,
        background: props.active ? activeBg : "transparent",
        color: props.active ? "#fff" : "#555",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        ":hover": { background: props.active ? activeBg : "#f5f5f5" },
    };
});

export const CSTabBadge = styled("span")(function (props) {
    return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "20px",
        height: "20px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 700,
        padding: "0 6px",
        background: props.active ? "rgba(255,255,255,0.25)" : "#e0e0e0",
        color: props.active ? "#fff" : "#666",
    };
});

/* ─── Section Card ─── */
export const CSSectionCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    marginBottom: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflow: "hidden",
});

export const CSSectionHeader = styled("div")(function (props) {
    const color = props.color || PRIMARY;
    return {
        padding: "12px 18px",
        fontWeight: 700,
        fontSize: "0.88rem",
        color: "#fff",
        background: color,
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };
});

export const CSSectionBody = styled("div")({
    padding: "16px 18px",
});

/* ─── Form Elements ─── */
export const CSFormRow = styled("div")(function (props) {
    return {
        display: "grid",
        gridTemplateColumns: props.cols || "1fr 1fr",
        gap: "14px",
        marginBottom: "14px",
        "@media(max-width: 768px)": { gridTemplateColumns: "1fr" },
    };
});

export const CSFormGroup = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
});

export const CSLabel = styled("label")({
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#444",
    display: "flex",
    alignItems: "center",
    gap: "4px",
});

export const CSRequired = styled("span")({
    color: DANGER,
    fontWeight: 700,
});

export const CSInput = styled("input")({
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "0.84rem",
    outline: "none",
    transition: "border-color 0.2s",
    ":focus": { borderColor: PRIMARY },
    "::placeholder": { color: "#bbb" },
});

export const CSSelect = styled("select")({
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "0.84rem",
    outline: "none",
    background: "#fff",
    cursor: "pointer",
    ":focus": { borderColor: PRIMARY },
});

export const CSTextarea = styled("textarea")({
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "0.84rem",
    outline: "none",
    minHeight: "80px",
    resize: "vertical",
    fontFamily: "inherit",
    ":focus": { borderColor: PRIMARY },
    "::placeholder": { color: "#bbb" },
});

/* ─── Result Cards (Radio-style selection) ─── */
export const CSResultGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "14px",
    "@media(max-width: 768px)": { gridTemplateColumns: "1fr" },
});

export const CSResultCard = styled("button")(function (props) {
    const color = props.resultColor || PRIMARY;
    return {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 16px",
        border: props.selected ? "2px solid " + color : "1px solid #e0e0e0",
        borderRadius: "10px",
        background: props.selected ? color + "0a" : "#fafafa",
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.2s",
        ":hover": { borderColor: color, background: color + "08" },
    };
});

export const CSResultIcon = styled("span")({
    fontSize: "1.5rem",
    lineHeight: 1,
});

export const CSResultContent = styled("div")({
    flex: 1,
});

export const CSResultLabel = styled("div")(function (props) {
    return {
        fontSize: "0.85rem",
        fontWeight: props.selected ? 700 : 500,
        color: props.selected ? props.color || SECONDARY : "#555",
    };
});

export const CSResultDesc = styled("div")({
    fontSize: "0.73rem",
    color: "#888",
    marginTop: "2px",
});

/* ─── Treatment Options ─── */
export const CSTreatmentGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "8px",
    marginBottom: "14px",
    "@media(max-width: 768px)": { gridTemplateColumns: "1fr 1fr" },
});

export const CSTreatmentItem = styled("label")(function (props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 14px",
        borderRadius: "8px",
        border: props.checked
            ? "2px solid " + (props.color || PRIMARY)
            : "1px solid #e0e0e0",
        background: props.checked
            ? (props.color || PRIMARY) + "0a"
            : "#fafafa",
        cursor: "pointer",
        fontSize: "0.8rem",
        fontWeight: props.checked ? 600 : 400,
        color: props.checked ? (props.color || PRIMARY) : "#555",
        transition: "all 0.2s",
        ":hover": { borderColor: props.color || PRIMARY, background: (props.color || PRIMARY) + "08" },
    };
});

export const CSCheckBox = styled("input")({
    accentColor: PRIMARY,
    width: "16px",
    height: "16px",
    cursor: "pointer",
});

/* ─── Alert Banners ─── */
export const CSAlertBanner = styled("div")(function (props) {
    const colors = {
        critical: { bg: "#fce4ec", border: "#ef9a9a", icon: DANGER },
        warning: { bg: "#fff8e1", border: "#ffe082", icon: WARNING },
        info: { bg: "#e3f2fd", border: "#90caf9", icon: INFO },
        success: { bg: "#e8f5e9", border: "#a5d6a7", icon: SUCCESS },
    };
    const c = colors[props.type] || colors.info;
    return {
        display: "flex",
        gap: "12px",
        padding: "12px 16px",
        borderRadius: "8px",
        background: c.bg,
        border: "1px solid " + c.border,
        marginBottom: "12px",
        alignItems: "flex-start",
    };
});

export const CSAlertIcon = styled("span")({ fontSize: "1.3rem", lineHeight: 1 });
export const CSAlertContent = styled("div")({ flex: 1, display: "flex", flexDirection: "column", gap: "2px" });
export const CSAlertTitle = styled("span")({ fontWeight: 700, fontSize: "0.82rem", color: "#333" });
export const CSAlertDetail = styled("span")({ fontSize: "0.78rem", color: "#555" });

/* ─── Follow-up Info Card ─── */
export const CSFollowUpCard = styled("div")({
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
    padding: "14px 18px",
    background: PRIMARY_BG,
    borderRadius: "8px",
    marginBottom: "14px",
    border: "1px solid " + PRIMARY + "30",
});

export const CSFollowUpStat = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "2px",
});

export const CSFollowUpLabel = styled("span")({
    fontSize: "0.7rem",
    fontWeight: 600,
    color: PRIMARY,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
});

export const CSFollowUpValue = styled("span")(function (props) {
    return {
        fontSize: "0.95rem",
        fontWeight: 700,
        color: props.alert ? DANGER : (props.success ? SUCCESS : SECONDARY),
    };
});

/* ─── Action Bar ─── */
export const CSActionBar = styled("div")({
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    padding: "16px 0",
    flexWrap: "wrap",
});

export const CSPrimaryBtn = styled("button")(function (props) {
    return {
        padding: "10px 24px",
        borderRadius: "8px",
        border: "none",
        background: props.disabled ? "#bbb" : PRIMARY,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.84rem",
        cursor: props.disabled ? "not-allowed" : "pointer",
        transition: "background 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        ":hover": { background: props.disabled ? "#bbb" : PRIMARY_LIGHT },
    };
});

export const CSSecondaryBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    background: "#fff",
    color: "#555",
    fontWeight: 500,
    fontSize: "0.84rem",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    ":hover": { background: "#f5f5f5", borderColor: "#bbb" },
});

export const CSDangerBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "none",
    background: DANGER,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.84rem",
    cursor: "pointer",
    ":hover": { background: "#b71c1c" },
});

/* ─── Timeline Styles ─── */
export const CSTimelineContainer = styled("div")({
    position: "relative",
    paddingLeft: "24px",
    "&::before": {
        content: '""',
        position: "absolute",
        left: "10px",
        top: "0",
        bottom: "0",
        width: "2px",
        background: PRIMARY + "40",
    },
});

export const CSTimelineItem = styled("div")(function (props) {
    let dotColor = PRIMARY;
    if (props.result === "negative") dotColor = SUCCESS;
    if (props.result === "positive") dotColor = WARNING;
    if (props.result === "suspicious" || props.result === "cancer_suspected") dotColor = DANGER;
    return {
        position: "relative",
        marginBottom: "20px",
        "&::before": {
            content: '""',
            position: "absolute",
            left: "-18px",
            top: "16px",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: dotColor,
            border: "2px solid " + dotColor + "30",
        },
    };
});

export const CSTimelineCard = styled("div")({
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: "10px",
    overflow: "hidden",
    transition: "box-shadow 0.2s",
    ":hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
});

export const CSTimelineHeader = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: "#f9f9f9",
    borderBottom: "1px solid #eee",
    flexWrap: "wrap",
    gap: "8px",
    cursor: "pointer",
});

export const CSTimelineDate = styled("span")({
    fontSize: "0.8rem",
    fontWeight: 600,
    color: PRIMARY,
});

export const CSTimelineResult = styled("span")(function (props) {
    let color = SECONDARY;
    if (props.result === "negative") color = SUCCESS;
    if (props.result === "positive") color = "#e65100";
    if (props.result === "suspicious") color = DANGER;
    if (props.result === "cancer_suspected") color = "#b71c1c";
    return {
        fontSize: "0.82rem",
        fontWeight: 700,
        color: color,
    };
});

export const CSTimelineBody = styled("div")(function (props) {
    return {
        padding: props.open ? "12px 16px" : "0 16px",
        maxHeight: props.open ? "2000px" : "0",
        overflow: "hidden",
        transition: "all 0.3s ease",
    };
});

export const CSTimelineField = styled("div")({
    display: "flex",
    gap: "8px",
    marginBottom: "6px",
    fontSize: "0.8rem",
});

export const CSTimelineFieldLabel = styled("span")({
    fontWeight: 600,
    color: "#666",
    minWidth: "140px",
});

export const CSTimelineFieldValue = styled("span")({
    color: "#333",
    flex: 1,
});

export const CSTimelineActions = styled("div")({
    display: "flex",
    gap: "8px",
    padding: "10px 16px",
    borderTop: "1px solid #eee",
});

export const CSTimelineBtn = styled("button")(function (props) {
    const color = props.color || PRIMARY;
    return {
        padding: "6px 14px",
        borderRadius: "6px",
        border: "1px solid " + color + "40",
        background: color + "0a",
        color: color,
        cursor: "pointer",
        fontSize: "0.76rem",
        fontWeight: 600,
        transition: "all 0.15s",
        ":hover": { background: color + "18" },
    };
});

/* ─── Empty State ─── */
export const CSEmptyState = styled("div")({
    textAlign: "center",
    padding: "40px 20px",
});

export const CSEmptyIcon = styled("div")({
    fontSize: "3rem",
    marginBottom: "12px",
});

export const CSEmptyTitle = styled("h3")({
    margin: "0 0 6px",
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#555",
});

export const CSEmptyDesc = styled("p")({
    margin: 0,
    fontSize: "0.85rem",
    color: "#888",
});

/* ─── Status Badge ─── */
export const CSStatusBadge = styled("span")(function (props) {
    const color = props.color || PRIMARY;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "0.72rem",
        fontWeight: 600,
        background: color + "15",
        color: color,
        border: "1px solid " + color + "30",
    };
});

/* ─── Tag Pill ─── */
export const CSTagPill = styled("span")(function (props) {
    const color = props.color || SECONDARY;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 600,
        background: color + "12",
        color: color,
    };
});

/* ─── Validation Error ─── */
export const CSFieldError = styled("span")({
    fontSize: "0.72rem",
    color: DANGER,
    fontWeight: 500,
    marginTop: "2px",
});

/* ─── Procedure Blocked Notice ─── */
export const CSProcedureBlocked = styled("div")({
    textAlign: "center",
    padding: "48px 20px",
});

export const CSProcedureBlockedTitle = styled("p")({
    fontWeight: 600,
    fontSize: "1rem",
    margin: "0 0 8px",
});

export const CSProcedureBlockedText = styled("p")({
    fontSize: "0.9rem",
    margin: "0 0 16px",
    lineHeight: 1.6,
});

export const CSProcedureBlockedLink = styled("a")({
    fontSize: "0.875rem",
    textDecoration: "underline",
    color: PRIMARY,
    "&:hover": { color: "#004070" },
});

/* ─── Loading ─── */
export const CSLoading = styled("div")({
    textAlign: "center",
    padding: "60px 20px",
    fontSize: "1rem",
    color: "#888",
});
